import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal
} from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import {
  ICalendarEvent,
  NgAddToCalendarService
} from "src/libs/ng-add-to-calendar";
import { FormService } from "src/app/services/form.service";
import { DealerResponse } from "src/domain/client";
import { DatePipe } from "@angular/common";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { RouterLink } from "@angular/router";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { RequestFailedComponent } from "../../layout/request-failed/request-failed.component";
import { UpsalesContainerComponent } from "./components/upsales-container/upsales-container.component";
import {
  BookingState,
  selectBookingError,
  selectBookingState,
  selectIsBookingMadeSuccessful,
  selectIsLoading,
  selectOrderId,
  selectSelectedDealer,
  submitBooking
} from "src/app/state";
import { Store } from "@ngrx/store";

type IBookingError = {
  title: string;
  subtitle: string;
  returnText: string;
  returnToPage: number;
  returnButtonAbove: boolean;
};

@Component({
  selector: "app-complete",
  templateUrl: "./complete.component.html",
  providers: [DatePipe],
  imports: [
    RouterLink,
    MatProgressSpinner,
    RequestFailedComponent,
    UpsalesContainerComponent,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompleteComponent extends PageBaseComponent implements OnInit {
  public googleCalendar: SafeUrl;
  public outlookCalendar: SafeUrl;
  public outlookLive: SafeUrl;
  public iCalendar: SafeUrl;
  public yahooCalendar: SafeUrl;

  public newEvent: ICalendarEvent;

  readonly bookingState: Signal<BookingState>;
  readonly isLoading: Signal<boolean>;
  readonly orderIdSignal: Signal<string | null>;
  readonly bookingErrorSignal: Signal<IBookingError | null>;
  readonly isBookingMadeSuccessful: Signal<boolean>;
  readonly selectedWorkshop: Signal<DealerResponse>;

  constructor(
    public formService: FormService,
    public translateService: TranslateService,
    private addToCalendarService: NgAddToCalendarService,
    private sanitizer: DomSanitizer,
    private datePipe: DatePipe,
    private store: Store
  ) {
    super();
    if (!this.formService.isEntireBookingValid) {
      return;
    }

    this.bookingState = this.store.selectSignal(selectBookingState);
    this.isLoading = this.store.selectSignal(selectIsLoading);
    this.orderIdSignal = this.store.selectSignal(selectOrderId);
    this.bookingErrorSignal = this.store.selectSignal(selectBookingError);
    this.isBookingMadeSuccessful = this.store.selectSignal(
      selectIsBookingMadeSuccessful
    );

    this.selectedWorkshop = this.store.selectSignal(selectSelectedDealer);

    this.newEvent = {
      title: "Verkstadsbesök " + formService.carInfoResponse.registrationNumber,
      start: formService.timeSelectionForm.controls.date.value,
      duration: 60,
      address:
        this.formService.selectedWorkshop.street +
        ", " +
        this.formService.selectedWorkshop.city,
      description: this.generateBookingDescription()
    };
    this.googleCalendar = this.sanitizeCalendarUrl(
      this.addToCalendarService.getHrefFor(
        this.addToCalendarService.calendarType.google,
        this.newEvent
      )
    );

    this.outlookCalendar = this.sanitizeCalendarUrl(
      this.addToCalendarService.getHrefFor(
        this.addToCalendarService.calendarType.outlook,
        this.newEvent
      )
    );

    this.outlookLive = this.sanitizeCalendarUrl(
      this.addToCalendarService.getHrefFor(
        this.addToCalendarService.calendarType.outlookLive,
        this.newEvent
      )
    );

    this.iCalendar = this.sanitizeCalendarUrl(
      this.addToCalendarService.getHrefFor(
        this.addToCalendarService.calendarType.iCalendar,
        this.newEvent
      )
    );

    this.yahooCalendar = this.sanitizeCalendarUrl(
      this.addToCalendarService.getHrefFor(
        this.addToCalendarService.calendarType.yahoo,
        this.newEvent
      )
    );
  }

  ngOnInit(): void {
    if (!this.formService.isEntireBookingValid) {
      return;
    }
    if (this.isBookingMadeSuccessful()) {
      return;
    }

    this.store.dispatch(submitBooking());
  }

  returnClicked() {
    if (this.bookingErrorSignal()) {
      this.formService.invalidateForm(
        this.bookingErrorSignal().returnToPage - 1
      );
    }
  }

  private generateBookingDescription(): string {
    const parts = [];
    const workshops = this.bookingState().selectedWorkshopSystems;
    const translatedWorkshops = workshops.map((workshop) =>
      this.translateService.instant(
        "workshopSystemEnum." + workshop.toLocaleLowerCase()
      )
    );

    let packageDescription = "";
    const products = this.formService.getSelectedTimeSelectionJobsAsProducts();

    products.forEach((product) => {
      const selected = this.formService.selectedTimePackages.find(
        (j) => j.packageId === +product.item_id
      );
      if (!selected) return;

      const selectedPackage = this.bookingState().basePackages.find(
        (p) => +p.id == selected.packageId
      );
      if (!selectedPackage) return;

      packageDescription = selectedPackage.description.shortDescription;
    });

    if (translatedWorkshops) {
      parts.push(translatedWorkshops.join(", "));
    }
    if (packageDescription) {
      parts.push(packageDescription);
    }

    return parts.join(",\n");
  }

  private sanitizeCalendarUrl(url: string): SafeUrl {
    if (!url) {
      return "";
    }

    if (url.startsWith("data:text/calendar;charset=utf8,")) {
      return this.sanitizer.bypassSecurityTrustUrl(url);
    }

    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return this.sanitizer.bypassSecurityTrustUrl(parsed.href);
      }
    } catch {
      console.error("Invalid calendar URL:", url);
    }

    return "";
  }
}
